import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useChatContext } from '../../context/ChatContext'
import { useWidgetContext } from '../../context/WidgetContext'
import { Mic, Pause } from 'react-bootstrap-icons'
import { MessageList } from './MessageList'
import { SendButton } from './SendButton'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'
import type { AgentWidgetMode } from '../../types/agent-mode'

export const AIChatAgent = ({ mode = 'chat' }: { mode?: AgentWidgetMode }) => {
  const { hasError, sendMessage, isChatLoading, stopMessage, resetChat } = useChatContext()
  const { consumeQueuedPrompt, queuedPromptVersion, config } = useWidgetContext()

  const isSpeechSupported = useMemo(() => {
    if (typeof window === 'undefined') return false
    return SpeechRecognition.browserSupportsSpeechRecognition()
  }, [])

  const { listening, resetTranscript, transcript } = useSpeechRecognition()

  // IMPORTANT: make this a RefObject, not a generic React.Ref
  const scrollableDivRef = useRef<HTMLDivElement>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setInputValue(transcript)
  }, [transcript])

  useEffect(() => {
    if (!isSpeechSupported) {
      console.warn('Your browser does not support speech recognition software')
    }
  }, [isSpeechSupported])

  const submitPrompt = useCallback(async (promptValue: string) => {
    if (listening) await SpeechRecognition.stopListening()
    if (!promptValue.trim() || isChatLoading) return

    setIsLoading(true)

    try {
      await sendMessage(promptValue)
      resetTranscript()
    } finally {
      setIsLoading(false)
    }
  }, [isChatLoading, listening, resetTranscript, sendMessage])

  const handleSendMessage = async () => {
    if (listening) await SpeechRecognition.stopListening()
    if (!inputValue.trim() || isChatLoading) return

    const value = inputValue
    setInputValue('')
    await submitPrompt(value)
  }

  useEffect(() => {
    const queued = consumeQueuedPrompt()
    if (!queued) return
    void submitPrompt(queued)
  }, [consumeQueuedPrompt, queuedPromptVersion, config, submitPrompt])

  const placeholder =
    mode === 'search'
      ? 'Search for anything...'
      : mode === 'speech'
        ? 'Speak or type to start...'
        : 'Send a message'

  return (
    <div
      className={`osirus-agent-panel-frame ai-chat-panel ${
        mode === 'speech' ? 'mode-speech' : ''
      }`}
    >
      <div>
        <MessageList listRef={scrollableDivRef} showError={hasError} />

        <div className='bottom-chat-container'>
          {config.mobileMcpToolBridge && <button type="button" onClick={resetChat} disabled={isChatLoading} aria-label="New conversation">New</button>}
          <div className='message-input-container'>
            <input
              type='text'
              className='message-input'
              placeholder={placeholder}
              tabIndex={0}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleSendMessage()
              }}
            />

            {isSpeechSupported && (
              <button
                type='button'
                className='message-input-microphone'
                onClick={() => {
                  if (listening) {
                    SpeechRecognition.stopListening()
                  } else {
                    SpeechRecognition.startListening({ continuous: true })
                  }
                }}
              >
                {listening ? <Pause /> : <Mic />}
              </button>
            )}
          </div>

          {config.mobileMcpToolBridge && isChatLoading ? <button type="button" onClick={stopMessage}>Stop</button> : <SendButton
            onClick={handleSendMessage}
            isLoading={isLoading || isChatLoading}
            isDisabled={inputValue.length === 0}
          />}
        </div>
      </div>
    </div>
  )
}
